"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { cn } from "@/lib/utils";
import { ArrowRight } from "@/components/icons";
import { HeroGlow } from "@/components/ui";
import { buttonVariants } from "@/components/ui/button";

import dynamic from "next/dynamic";

const WaitlistModal = dynamic(
  () => import("@/components/sections/home/products-overview/waitlist-modal"),
  { ssr: false, loading: () => null }
);
const IspWaitlistModal = dynamic(
  () => import("@/components/sections/home/products-overview/isp-waitlist-modal"),
  { ssr: false, loading: () => null }
);
import { pricingProducts, cardVariants, containerVariants, type WaitlistTarget } from "./data";

// Visitors referred by the "Buy Proxies" button in the browser extension are
// already mid-purchase — they land expecting a direct path to checkout, not a
// generic product chooser, so this segment skips the waitlist-only cards and
// gets more transactional copy.
function PricingOverviewSearchParamsGate() {
  const searchParams = useSearchParams();
  const isExtensionReferral = searchParams.get("utm_source") === "extension";
  return <PricingOverviewContent isExtensionReferral={isExtensionReferral} />;
}

export default function PricingOverviewSection() {
  return (
    <Suspense fallback={<PricingOverviewContent isExtensionReferral={false} />}>
      <PricingOverviewSearchParamsGate />
    </Suspense>
  );
}

interface PricingOverviewContentProps {
  isExtensionReferral: boolean;
}

function PricingOverviewContent({ isExtensionReferral }: PricingOverviewContentProps) {
  const reduced = useReducedMotion() ?? false;
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [waitlistTarget, setWaitlistTarget] = useState<WaitlistTarget | null>(null);
  const router = useRouter();

  const products = isExtensionReferral
    ? pricingProducts.filter((product) => !product.waitlist)
    : pricingProducts;

  return (
    <>
      <section
        className="pb-section-sm border-border relative overflow-y-clip border-b pt-24 sm:pt-32"
        aria-labelledby="pricing-heading"
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <HeroGlow align="center" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-14 max-w-2xl text-center">
            <h1
              id="pricing-heading"
              className="text-foreground mb-4 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
            >
              {isExtensionReferral ? "Continue your purchase" : "Plans for every proxy network"}
            </h1>
            <p className="text-muted-foreground light:text-foreground/80 mx-auto max-w-xl text-lg leading-relaxed">
              {isExtensionReferral
                ? "Pick the proxy network you want to buy — pricing starts at $1/GB, no contracts."
                : "From premium pooled mobile IPs to global residential IPs — pick the network that fits your workload."}
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial={reduced ? "visible" : "hidden"}
            whileInView="visible"
            viewport={scrollRevealViewport}
            className={cn(
              "grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4",
              isExtensionReferral ? "xl:grid-cols-2" : "xl:grid-cols-4"
            )}
          >
            {products.map((product) => {
              const { Visual } = product;
              const isHovered = hoveredId === product.id;
              const isWaitlist = "waitlist" in product && !!product.waitlist;

              const handleCardClick = () => {
                if (isWaitlist) setWaitlistTarget(product.waitlist!);
                else router.push(product.href!);
              };

              const ctaClassName = cn(
                buttonVariants({ variant: isHovered ? "default" : "outline", size: "sm" }),
                "w-fit font-medium mt-1 transition-all duration-300"
              );
              const ctaStyle = isHovered
                ? {
                    backgroundColor: "var(--primary)",
                    color: "var(--primary-foreground)",
                    borderColor: "transparent",
                  }
                : {};

              return (
                <motion.div
                  key={product.id}
                  variants={cardVariants}
                  whileHover={reduced ? undefined : { y: -4, transition: { duration: 0.2 } }}
                  onHoverStart={() => setHoveredId(product.id)}
                  onHoverEnd={() => setHoveredId(null)}
                  onClick={handleCardClick}
                  className={cn(
                    "bg-card relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border transition-all duration-300",
                    isHovered ? "border-brand/35 ring-brand/10 shadow-md ring-1" : "border-border"
                  )}
                >
                  {product.badge && (
                    <span
                      className="bg-background/70 absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold tracking-wider uppercase backdrop-blur-sm"
                      style={{
                        color: "var(--brand-green)",
                        borderColor: "color-mix(in srgb, var(--brand-green) 33%, transparent)",
                      }}
                    >
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: "var(--brand-green)" }}
                        aria-hidden="true"
                      />
                      {product.badge}
                    </span>
                  )}

                  <div className="h-[200px] w-full">
                    <Visual reduced={reduced} hovered={isHovered} />
                  </div>

                  <div className="flex flex-1 flex-col gap-3 px-5 py-5">
                    <div className="flex items-center justify-between gap-2">
                      <h2 className="text-foreground text-base font-semibold">{product.name}</h2>
                      {product.startingPrice && (
                        <span className="text-brand-green shrink-0 text-sm font-semibold">
                          {product.startingPrice}
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {product.description}
                    </p>
                    <ul className="flex flex-1 flex-col gap-1.5">
                      {product.bullets.map((b) => (
                        <li
                          key={b}
                          className="text-muted-foreground flex items-center gap-2 text-sm"
                        >
                          <span
                            className="h-1 w-1 shrink-0 rounded-full"
                            style={{ backgroundColor: "var(--brand-green)" }}
                          />
                          {b}
                        </li>
                      ))}
                    </ul>

                    {isWaitlist ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setWaitlistTarget(product.waitlist!);
                        }}
                        className={ctaClassName}
                        style={ctaStyle}
                      >
                        {product.cta}
                        <ArrowRight size={13} className="ml-1.5" aria-hidden="true" />
                      </button>
                    ) : (
                      <Link
                        href={product.href!}
                        onClick={(e) => e.stopPropagation()}
                        className={ctaClassName}
                        style={ctaStyle}
                      >
                        {product.cta}
                        <ArrowRight size={13} className="ml-1.5" aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <WaitlistModal
        open={waitlistTarget === "datacenter"}
        onClose={() => setWaitlistTarget(null)}
      />
      <IspWaitlistModal open={waitlistTarget === "isp"} onClose={() => setWaitlistTarget(null)} />
    </>
  );
}
